import { Injectable } from '@angular/core'
import {
  Observable,
  catchError,
  map,
  shareReplay,
  tap,
  throwError
} from 'rxjs'
import { GraphService } from 'src/app/shared/lib/api/graph.service'
import {
  GET_ORDER,
  GET_ORDERS_AVAILABLE,
  GET_ORDERS_AVAILABLE_FOR_EGRESOS,
  GET_ORDERS_BY_PROVIDER
} from '../graphql/ordenes-servicio.queries'
import {
  CREATE_ORDER_PDF_DOWNLOAD_URL,
  UPDATE_ORDER_SCHEDULED_PAYMENT_DATE
} from '../graphql/ordenes-servicio.mutations'

export interface OrderPdfDownloadResult {
  pdfStatus: string
  lastPdfError: string | null
  errorCode: string | null
  url: string | null
  expiresAt: string | null
}

export interface ServiceOrderSummary {
  tipoOrden: 'Orden de Servicio'
  orderId: string
  orderNumber: string
  orderStatus: string
  currency: string
  total: number
  paidAt: string | null
  scheduledPaymentDate: string | null
  paymentCondition: string | null
  providerSnapshot: { id: string; name: string } | null
  fileSnapshot: { fileNumber: string; fileExtend?: string | null } | null
}

@Injectable({ providedIn: 'root' })
export class OrdenesServicioService {
  private readonly egresosPageCache = new Map<
    string,
    { expiresAt: number; response$: Observable<any> }
  >()
  private readonly egresosPageCacheTtlMs = 120_000

  constructor (private readonly graphService: GraphService) {}

  getOSById (id: string): Observable<any> {
    return this.graphService
      .queryGraphQLCore<{ getOrder: any }>({
        query: GET_ORDER,
        variables: { orderId: id }
      })
      .pipe(
        map(response => this.mapCoreOrderToLegacy(response.getOrder)),
        catchError(error => {
          console.error(`Error fetching OS ${id}:`, error)
          return throwError(
            () => new Error('Error al obtener orden de servicio')
          )
        })
      )
  }

  getAllByProvider (providerId: string): Observable<any> {
    return this.graphService
      .queryGraphQLCore<{ getOrdersByProviderId: any[] }>({
        query: GET_ORDERS_BY_PROVIDER,
        variables: { providerId }
      })
      .pipe(
        map(response => ({
          getAllOSByProvider: (response.getOrdersByProviderId ?? []).flatMap(
            order => this.mapCoreOrderLinesToLegacy(order)
          )
        }))
      )
  }

  getAllAvailable (
    _projectionExpression?: string,
    limit?: number,
    lastKey?: string
  ): Observable<any> {
    return this.getCoreOrdersPage(
      GET_ORDERS_AVAILABLE,
      'getOrdersAvailablePage',
      'getAllOSAvailable',
      limit,
      lastKey
    )
  }

  getAllAvailableForEgresos (
    limit?: number,
    lastKey?: string
  ): Observable<any> {
    const normalizedLimit = Math.min(Math.max(limit ?? 50, 1), 100)
    const cacheKey = `${normalizedLimit}:${lastKey ?? 'FIRST_PAGE'}`
    const cachedPage = this.egresosPageCache.get(cacheKey)

    if (cachedPage && cachedPage.expiresAt > Date.now()) {
      return cachedPage.response$
    }

    if (cachedPage) {
      // Todas las páginas pertenecen a la misma lectura lógica. Al expirar
      // una, se evita mezclar una primera página nueva con páginas antiguas.
      this.invalidateAvailableForEgresosCache()
    }

    const response$ = this.getCoreOrdersPage(
      GET_ORDERS_AVAILABLE_FOR_EGRESOS,
      'getOrdersAvailableForEgresosPage',
      'getAllOSAvailableToCreateEgresos',
      normalizedLimit,
      lastKey,
      order => [this.mapCoreOrderToSummary(order)]
    ).pipe(
      catchError(error => {
        this.egresosPageCache.delete(cacheKey)
        return throwError(() => error)
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    )

    this.egresosPageCache.set(cacheKey, {
      expiresAt: Date.now() + this.egresosPageCacheTtlMs,
      response$
    })

    return response$
  }

  invalidateAvailableForEgresosCache (): void {
    this.egresosPageCache.clear()
  }

  updateScheduledPaymentDate (
    orderId: string,
    scheduledPaymentDate: string
  ): Observable<any> {
    return this.graphService.mutateGraphQLCore<any>({
      mutation: UPDATE_ORDER_SCHEDULED_PAYMENT_DATE,
      variables: {
        input: {
          orderId,
          scheduledPaymentDate
        }
      }
    }).pipe(tap(() => this.invalidateAvailableForEgresosCache()))
  }

  getOrderPdfDownloadUrl (
    orderId: string,
    disposition: 'inline' | 'attachment' = 'inline'
  ): Observable<OrderPdfDownloadResult> {
    return this.graphService
      .mutateGraphQLCore<{
        createOrderPdfDownloadUrl: OrderPdfDownloadResult
      }>({
        mutation: CREATE_ORDER_PDF_DOWNLOAD_URL,
        variables: { orderId, disposition }
      })
      .pipe(map(response => response.createOrderPdfDownloadUrl))
  }

  private getCoreOrdersPage (
    query: string,
    coreResponseKey: string,
    legacyResponseKey: string,
    limit?: number,
    lastKey?: string,
    mapOrderLines: (order: any) => any[] = order =>
      this.mapCoreOrderLinesToLegacy(order)
  ): Observable<any> {
    return this.graphService
      .queryGraphQLCore<any>({
        query,
        variables: {
          input: {
            limit: Math.min(Math.max(limit ?? 50, 1), 100),
            lastKey: lastKey ?? null
          }
        }
      })
      .pipe(
        map(response => {
          const page = response[coreResponseKey] ?? {}
          return {
            [legacyResponseKey]: {
              items: (page.items ?? []).flatMap((order: any) =>
                mapOrderLines(order)
              ),
              lastKey: page.lastKey ?? null
            }
          }
        })
      )
  }

  private mapCoreOrderLinesToLegacy (order: any): any[] {
    const lines = order?.lines?.length ? order.lines : [undefined]
    return lines.map((line: any) => this.mapCoreOrderToLegacy(order, line))
  }

  mapCoreOrderToSummary (order: any): ServiceOrderSummary {
    return {
      tipoOrden: 'Orden de Servicio',
      orderId: order.orderId,
      orderNumber: order.orderNumber,
      orderStatus: order.orderStatus,
      currency: order.currency,
      total: order.total,
      paidAt: order.paidAt,
      scheduledPaymentDate: order.scheduledPaymentDate,
      paymentCondition: order.paymentCondition,
      providerSnapshot: order.providerSnapshot,
      fileSnapshot: order.fileSnapshot
    }
  }

  private mapCoreOrderToLegacy (order: any, line?: any): any {
    if (!order) return null

    return {
      id: order.orderId,
      numberOS: order.orderNumber,
      estadoOS:
        order.orderStatus === 'CONFIRMADO' ? 'CONFIRMED' : order.orderStatus,
      monedaTarifa: order.currency,
      totalGeneral: line?.amount ?? order.total,
      fechaPago: order.paidAt,
      fechaPagoProgramada: order.scheduledPaymentDate,
      codigoReserva: order.reservationNumber,
      usuario: order.requestedByName,
      email: order.requestedByEmail,
      nameProveedor: order.providerSnapshot?.name,
      nameClient: order.fileSnapshot?.clientName,
      numberFile: order.fileSnapshot?.fileNumber,
      fileExtend: order.fileSnapshot?.fileExtend,
      createdAt: order.createdAt,
      updatedAt: order.updatedAt,
      objProvider: {
        id: order.providerSnapshot?.id,
        nombre: order.providerSnapshot?.name
      },
      condicionPago: {
        condicionPago: order.paymentCondition
      },
      servicio: line
        ? {
            id: line.serviceId,
            fechaOperacion: line.serviceSnapshot?.operationDate,
            fechaPago: order.paidAt,
            fechaPagoProgramada: order.scheduledPaymentDate,
            tipoServicio: line.serviceSnapshot?.serviceType,
            categoriaServicio: line.serviceSnapshot?.serviceCategory
          }
        : undefined
    }
  }
}
